import { normalizeDataObject } from '../../utils/normalize';

/**
 * @typedef {import('../../types').CartTransferOptions} Options
 */

/**
 * @typedef {object} CartSettings
 * @property {boolean} isBufferMerged
 * @property {boolean} isDeliveryDisplay
 * @property {boolean} isPriceEnabledOnSite
 * @property {boolean} isLegalTextEnabled
 * @property {string} isoLocale
 * @property {string} [callUsURL]
 * @property {string} [whereToBuyURL] Distributor URL. Only set for B2B cart.
 * @property {string} [contactSalesURL] Only set for B2B cart.
 * @property {string} [getPricingCtaURL]
 * @property {string} [b2cBuyInStoreURL] Only set for B2C cart.
 * @property {string} [b2cFindElectricianURL] Only set for B2C cart.
 * @property {boolean} [hideComparatorCta=false] Hides "Compare" link on Operations block.
 * @property {boolean} [hidePills=false] Hide Pills block.
 * @property {boolean} [disableSubstitutions=false] Disables product substitutions loading.
 * @property {Options} [channelSightSettings]
 */

/** @type {CartSettings} */
const defaults = {
  isBufferMerged: false,
  isDeliveryDisplay: false,
  isPriceEnabledOnSite: false,
  isLegalTextEnabled: false,
  isWeeePricesEnabled: false,
  isoLocale: 'en-US',
  hideComparatorCta: false,
  hidePills: false,
  disableSubstitutions: false
};

export default class OptionsStore {
  callUsURL = '';

  isBufferMerged = false;

  isDeliveryDisplay = false;

  isPriceEnabledOnSite = false;

  isLegalTextEnabled = false;

  whereToBuyURL = '';

  /** ISO 639-1 pairs. */
  locale = '';

  currency = '';

  /** @type {Options} */
  channelSightSettings;

  rootStore = null;

  immediateUiUpdates = false; // Controls UI reaction on changes

  get isCartTransferAvailable() {
    return !!this.channelSightSettings;
  }

  /**
   * @param {CartSettings} initialSettings
   * @param {object} rootStore
   */
  constructor(initialSettings, rootStore) {
    const {
      isBufferMerged,
      isDeliveryDisplay,
      isPriceEnabledOnSite,
      isLegalTextEnabled,
      isWeeePricesEnabled,
      isoLocale,
      callUsURL,
      whereToBuyURL,
      contactSalesURL,
      getPricingCtaURL,
      b2cBuyInStoreURL,
      b2cFindElectricianURL,
      hideComparatorCta,
      hidePills,
      disableSubstitutions,
      channelSightSettings
    } = normalizeDataObject(initialSettings, { defaults });

    this.rootStore = rootStore;

    this.isDeliveryDisplay = isDeliveryDisplay;
    this.isPriceEnabledOnSite = isPriceEnabledOnSite;
    this.isLegalTextEnabled = isLegalTextEnabled;
    this.locale = isoLocale;
    this.callUsURL = callUsURL;
    this.whereToBuyURL = whereToBuyURL;
    this.contactSalesURL = contactSalesURL;
    this.getPricingCtaURL = getPricingCtaURL;
    this.b2cBuyInStoreURL = b2cBuyInStoreURL;
    this.b2cFindElectricianURL = b2cFindElectricianURL;
    this.isWeeePricesVisible = isWeeePricesEnabled && isPriceEnabledOnSite && isLegalTextEnabled;
    this.isBufferMerged = isBufferMerged;

    this.hideComparatorCta = hideComparatorCta;
    this.hidePills = hidePills;
    this.disableSubstitutions = disableSubstitutions;

    if (channelSightSettings) {
      this.channelSightSettings = {
        ...channelSightSettings,
        locale: isoLocale
      };
    }
  }
}
